const SIMILARITY_ACTIVATION_API_URL = '/api/design/trackActivation';

type SimilarityActivationResponse = {
  status?: string;
  score?: number;
};

const MAX_API_TRIGGER_COUNT = 100;
const COMPLETED_STATUS = 'completed';

// Delay in seconds, 5 minutes
let delayInSeconds = 5 * 60 * 1000;

let trackingInProgress = false;
let isTrackingAllowedChecked = true;
let apiTriggerCount = 0;

/**
 * Tracks activation of a similarity
 *
 * @param infographicId infographic ID
 * @returns SimilarityActivationResponse
 */
export const trackActivationOnce = async (
  infographicId: string,
  triggerPredicate?: () => boolean,
): Promise<SimilarityActivationResponse | undefined> => {
  try {
    if (trackingInProgress || !isTrackingAllowedChecked || !infographicId) return;

    // Add a limit to prevent infinite tracking api call
    if (apiTriggerCount > MAX_API_TRIGGER_COUNT) {
      throw new Error('Api call limit reached');
    }

    if (typeof triggerPredicate === 'function' && !triggerPredicate()) {
      return;
    }

    // Prevent api call while tracking is in progress
    trackingInProgress = true;

    // Delay api call
    await new Promise((resolve) => setTimeout(resolve, delayInSeconds));

    // Increase the api trigger count
    apiTriggerCount++;

    const response = await fetch(SIMILARITY_ACTIVATION_API_URL, {
      method: 'POST',
      body: JSON.stringify({
        id: infographicId,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    const { status, score } = (await response.json()) as SimilarityActivationResponse;

    // Non strict check score whether is a number
    if (isNaN(Number(score))) throw new Error('Score is not a number');

    // Clear tracking progress queue
    trackingInProgress = false;

    // Once the status is completed, we don't need to track the activation anymore
    if (status === COMPLETED_STATUS) {
      isTrackingAllowedChecked = false;
    }

    return {
      status,
      score,
    };
  } catch (error) {
    console.warn('Similarity activation: ', error);

    // If there is an error, stop tracking
    isTrackingAllowedChecked = false;
  }
};
